import _ from 'lodash';
import moment from 'moment';
import c from './../utils/constants';

// Checks for any explicit methodology properties in data, and if so
// uses the first one found as link. If not, uses default.
export const computeMethodologyLink = (awards, defaultLink) => {
  const overrideDocAward = _.find(awards, (award) => award.methodologyLink);
  return overrideDocAward ? overrideDocAward.methodologyLink : defaultLink;
};

/**
 * Convert edges to awards and assign an id property to each award
 * @param  {array} edges
 * @return {array} awards
 */
export const convertEdgesToAwards = (edges) => edges.map((edge, i) => ({ ...edge.node, id: `award-${i}` }));

export const filterAwards = (awards, filterBy) => {
  const { timePeriod, awardType } = filterBy;
  if (timePeriod && awardType) {
    return _.filter(awards, (award) => award.timePeriod === timePeriod && award.type === awardType);
  } else if (timePeriod) {
    return _.filter(awards, (award) => award.timePeriod === timePeriod);
  } else if (awardType) {
    return _.filter(awards, (award) => award.type === awardType);
  }

  return awards;
};

export const sortAlphabetically = (awards, key) => _.orderBy(awards, (award) => award[key], ['asc']);

export const sortByMostRecentDateSold = (awards) =>
  [...awards].sort((left, right) => moment(right.dateSold).diff(moment(left.dateSold)));

/**
 * @param  {Array} awards
 * @param  {Array|String} sectionOrder (optional) - indicates the desired iteration order of sections
 *
 * @return {Object} sections
 */
function groupAwardsByTimePeriodAndType(awards) {
  const sections = _.groupBy(awards, 'timePeriod');

  // Second level of grouping - by type
  Object.keys(sections).forEach((timePeriod) => {
    sections[timePeriod] = _.groupBy(sections[timePeriod], 'type');
  });

  return sections;
}

/**
 * @param  {Array} awards
 * @param  {String} groupBy. Examples: `type`, `timePeriod`, `timePeriodAndType`.
 */

export const groupAwards = (awards, groupBy) => {
  if (groupBy === 'timePeriodAndType') {
    return groupAwardsByTimePeriodAndType(awards);
  }

  return _.groupBy(awards, groupBy);
};

/**
 * Determine the order in which award type sections should be rendered
 *
 * @param  {String} template (optional)
 * The page template where awards are rendered. Example: 'location'.
 *
 * @param  {Object} awardTypeSections
 * An object containing the award type sections. Example: {isp: [...], carrier: [...]}
 *
 * @return {Array} Array of award types indicating the order in which the corresponding sections should be rendered.
 * Example: ['isp', 'carrier']
 */
export const computeAwardTypeSectionOrder = (template, awardTypeSections) => {
  let awardTypeSectionOrder;

  if (template === 'location' && _.size(awardTypeSections) > 1) {
    // Location template is a special case - we can guarantee that there is only
    // one award winner per timePeriod per awardType.
    // We need to re-order the award types so that the type that has the most recent award should go on top.
    awardTypeSectionOrder = sortByMostRecentDateSold(
      Object.keys(awardTypeSections).map((awardType) => awardTypeSections[awardType][0])
    ).map(({ type }) => type);
  } else {
    awardTypeSectionOrder = _.intersection(c.awardTypeOrder, Object.keys(awardTypeSections));
  }

  return awardTypeSectionOrder;
};

/**
 * @param  {Object} sections      sections generated by `groupAwards`
 * @param  {String} groupedBy        indicates the criteria by which awards are grouped
 * @param  {String} displayAwardProp the award key to be displayed in the navigation
 * @return {Object}                  navItems to be consumed by PageNav
 */

export const convertSectionsToNavItems = (sections, groupedBy, displayAwardProp = 'locationName', template) => {
  const timePeriodOrder = _.intersection(c.timePeriods, Object.keys(sections));
  const navigationGroups = c.navigationGroups;
  if (groupedBy === 'timePeriodAndType') {
    return timePeriodOrder.map((timePeriod) => {
      const awardTypeSectionOrder = computeAwardTypeSectionOrder(template, sections[timePeriod]);
      return {
        label: c.timePeriodLabels[timePeriod],
        items: awardTypeSectionOrder.map((type) => {
          return {
            label: c.awardTypes[type].label,
            noSubItemsIndent: true,
            items: sections[timePeriod][type].map((award) => ({
              label: award[displayAwardProp] || '',
              scrollTo: award.id
            }))
          };
        })
      };
    });
  } else if (groupedBy === 'timePeriod' || groupedBy === 'type') {
    const sectionOrder = groupedBy === 'type' ? computeAwardTypeSectionOrder(template, sections) : timePeriodOrder;
    const regionalSection = [];
    const worldSection = [];
    const countrySection = [];
    sectionOrder.forEach((key) => {

      let items = [];
      let sectionLabel = '';

      sections[key].forEach((award) => {

        if (sectionLabel !== (groupedBy === 'type' ? c.awardTypes[key].label : c.timePeriodLabels[key])) {
          sectionLabel = groupedBy === 'type' ? c.awardTypes[key].label : c.timePeriodLabels[key];
        }

        items = [];
        // Break out Regional Awards
        if (navigationGroups.regional && navigationGroups.regional[award.locationName]) {
          items.push(
            {
              label: award[displayAwardProp] || '',
              scrollTo: award.id
            }
          );
          if (regionalSection.length === 0 || (regionalSection[regionalSection.length - 1] && regionalSection[regionalSection.length - 1].label !== sectionLabel)) {
            regionalSection.push(
              {
                label: sectionLabel,
                noSubItemsIdent: false,
                items
              }
            );
          } else {
            regionalSection[regionalSection.length - 1].items.push(items[0]);
          }

        } else if (navigationGroups[award.locationName] && award.locationName === 'World') {
          // Break out World Award
          items.push(
            {
              label: award.winner || '',
              scrollTo: award.id
            }
          );
          if (worldSection.length === 0 || (worldSection[worldSection.length - 1] && worldSection[worldSection.length - 1].label !== sectionLabel)) {
            worldSection.push(
              {
                label: sectionLabel,
                noSubItemsIdent: false,
                items
              }
            );
          } else {
            worldSection[worldSection.length - 1].items.push(items[0]);
          }
        } else  {
          // the rest are country awards
          items.push(
            {
              label:  award[displayAwardProp] || '',
              scrollTo: award.id
            }
          );

          if (countrySection.length === 0 || (countrySection[countrySection.length - 1] && countrySection[countrySection.length - 1].label !== sectionLabel)) {
            countrySection.push(
              {
                label: sectionLabel,
                noSubItemsIdent: false,
                items
              }
            );
          } else {
            countrySection[countrySection.length - 1].items.push(items[0]);
          }

        }
      });




    });

    return { world: worldSection, regional: regionalSection, countries: countrySection };

  }

  return null;
};
